<template>
  <form>
    <h3 class="text-size-lg-md weight--heavier layout--margin-bottom-md">
      3. Summary
    </h3>
    <v-wait for="loading change summary">
      <spring-spinner
        :animation-duration="1500"
        :size="53"
        :color="'#b6bbff'"
        transition="fade"
        mode="out-in"
        :duration="1000"
        enter-active-class="enter-active"
        slot="waiting"
      />
      <div
        class="account__form-section account__form-section--wide"
        v-if="changeSummary"
      >
        <dl class="layout--flex-start layout--flex-wrap">
          <div class="layout--margin-right-xl layout--margin-top-sm">
            <dt class="text-size-sm weight--bold">New Plan</dt>
            <dd class="text-size-default layout--margin-top-xs">
              {{ changeSummary.newPlanLabel }}
            </dd>
          </div>
          <div class="layout--margin-right-xl layout--margin-top-sm">
            <dt class="text-size-sm weight--bold">Available Credits</dt>
            <dd class="text-size-default layout--margin-top-xs">
              {{ changeSummary.newCreditTotalFormatted }}
            </dd>
          </div>
          <div
            v-if="subscriptionType !== 0"
            class="layout--margin-right-xl layout--margin-top-sm"
          >
            <dt class="text-size-sm weight--bold">Billing Period</dt>
            <dd class="text-size-default layout--margin-top-xs">
              {{ price }} billed {{ billingPeriod }}
            </dd>
          </div>
        </dl>
        <Divider dark class="layout--margin-top-lg layout--margin-bottom-md" />
        <dl class="layout--flex-start layout--flex-wrap">
          <div class="layout--margin-right-xl layout--margin-top-sm">
            <dt class="text-size-sm weight--bold">Transaction amount</dt>
            <dd class="text-size-default layout--margin-top-xs">
              <strong>${{ changeSummary.transactionAmountFormatted }}</strong>
              ({{ planChange | capitalizeFirstLetter }} today)
            </dd>
          </div>
          <div class="layout--margin-right-xl layout--margin-top-sm">
            <dt class="text-size-sm weight--bold">Credit Card</dt>
            <dd class="text-size-default">
              <CreditCard
                :classes="!defaultCard ? 'layout--margin-top-xs' : ''"
                :buttonText="defaultCard ? 'Edit Card Information' : 'Add Card'"
                secondary
              />
              <Error
                class="layout--margin-bottom-sm"
                :invalid="!defaultCard && $v.agreement.$dirty"
                message="You must add a card in order to continue"
              />
            </dd>
          </div>
        </dl>
        <v-checkbox v-model="agreement">
          <template v-slot:label>
            <div>
              I agree to the
              <a href="http://motionmailapp.com/terms" class="link" @click.stop
                >Terms of Use</a
              >
              and
              <a
                href="http://motionmailapp.com/privacy"
                class="link"
                @click.stop
                >Privacy Policy</a
              >
            </div>
          </template>
        </v-checkbox>
        <Error
          class="layout--margin-bottom-sm"
          :invalid="!$v.agreement.sameAs && $v.agreement.$dirty"
          message="You must agree in order to continue"
        />
        <div 
          v-if="downgradeMessage" 
          class="transaction-summary__message layout--margin-bottom-md"
        >
          <IconAlert class="dashboard__icon" />
          <p>{{ downgradeMessage }}</p>
        </div>
        <LoadingButton
          big
          :event="handleSubmit"
          label="Submit"
          :loading="$wait.is('changing subscription')"
          :doneLoading="saved"
        />
      </div>
    </v-wait>
  </form>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { mapWaitingActions } from "vue-wait";
import { sameAs } from "vuelidate/lib/validators";

import Divider from "../../../components/Divider";
import LoadingButton from "../../../components/LoadingButton";
import Error from "../../../components/Error";

import {
  getBillingCycleDescription,
  getBillingTransactionTypeDescription,
  getCreditCardIcon,
} from "../../../helpers/helpers";

import { changeSummary } from "../../../api/api";

import SuccessModal from "./SuccessModal";
import UpdateCardModal from "../../../components/UpdateCardModal";
import CreditCard from "../../../components/CreditCard";

import IconAlert from "../../../assets/icons/iconAlertBar-Exclamation.svg";

export default {
  name: "planSummary",
  components: {
    LoadingButton,
    Error,
    CreditCard,
    Divider,
    IconAlert,
  },
  mounted: function() {
    if (this.sortedSubscriptionOptions.length === 0) {
      this.getSubscriptionOptions();
    }
    if (!this.changeSummary) {
      this.getChangeSummary({
        subscriptionType: this.$route.params.subscriptionType,
        billingCycle: parseInt(this.$route.params.billingPeriod),
      }).then(result => {
        this.changeSummary = result.changeSummary;
      });
    }
  },
  data() {
    return { agreement: false, changeSummary: null, saved: false };
  },
  methods: {
    ...mapActions("account", ["getSubscriptionOptions"]),
    ...mapWaitingActions("account", {
      getChangeSummary: "loading change summary",
      changeSubscription: "changing subscription",
    }),
    handleSubmit: function() {
      // touch fields so they display relevent errors if needed
      this.$v.agreement.$touch();

      if (!this.$v.agreement.$invalid && this.defaultCard) {
        this.saved = false;
        const payload = {
          subscriptionType: this.subscriptionType,
          billingCycle: parseInt(this.$route.params.billingPeriod),
        };
        this.changeSubscription(payload).then(() => {
          this.saved = true;
          this.$router.push({ name: "confirmation" });
        });
      }
    },
    updateCreditCardModal: function() {
      this.$modal.show(UpdateCardModal);
    },
  },
  computed: {
    ...mapGetters("account", [
      "sortedSubscriptionOptions",
      "subscription",
      "defaultCard",
    ]),
    subscriptionType: function() {
      return parseInt(this.$route.params.subscriptionType);
    },
    downgradeMessage: function() {
      const change = this.changeSummary;
      const isDowngrade = change.planChangeAction === "Downgrade";
      const from = change.currentPlanLabel;
      const to = change.newPlanLabel;

      // any to free
        if (isDowngrade && this.planChange === "refund" && change.transactionAmount > 0) { // to === "Free") {
        return `
          You will be reimbursed for unused credits up to your current plan's
          monthly allowance. Rollover credits cannot be transferred between plans
          and will be lost upon downgrade.`;
      }
      // paid to paid downgrade
      if (isDowngrade) {
        return `
          Rollover credits cannot be transferred between plans and will be lost
          upon downgrade.`;
      }
    },
    creditCardIcon: function() {
      return getCreditCardIcon(this.defaultCard.cardType);
    },
    currentSubscription: function() {
      if (
        this.sortedSubscriptionOptions.length > 0 &&
        typeof this.subscription.subscriptionType !== "undefined"
      ) {
        return this.sortedSubscriptionOptions.find(
          s =>
            s.subscriptionType === parseInt(this.subscription.subscriptionType)
        );
      }
      return { levelOrder: 0 };
    },
    newSubscription: function() {
      if (
        this.sortedSubscriptionOptions.length > 0 &&
        this.$route.params.subscriptionType !== undefined
      ) {
        return this.sortedSubscriptionOptions.find(
          s => s.subscriptionType === this.subscriptionType
        );
      }
      return { levelOrder: 0 };
    },
    billingPeriod: function() {
      if (this.$route.params.billingPeriod !== undefined) {
        return getBillingCycleDescription(
          parseInt(this.$route.params.billingPeriod)
        );
      }
    },
    price: function() {
      return this.billingPeriod === "monthly"
        ? this.newSubscription.monthlyPriceLabel
        : this.newSubscription.annualPriceLabel;
    },
    // returns "refund", "charge", or "none" depending on billing type
    planChange: function() {
      if (this.changeSummary) {
        return getBillingTransactionTypeDescription(
          this.changeSummary.transactionType
        );
      }
      return "";
    },
  },
  validations: {
    agreement: {
      sameAs: sameAs(() => true),
    },
  },
};
</script>

<style>
.transaction-summary__message {
  display: flex;
  gap: 0.5rem;
  max-width: 35rem;
}
.transaction-summary__message > svg {
  flex: 1 0 auto;
}
</style>
